/**
 * 软键盘弹起后，不同移动浏览器对布局视口和可见视口的处理并不一致：
 * Chromium 可以通过 viewport meta 的 interactive-widget=resizes-visual 保持布局视口稳定，
 * 但不识别该声明的浏览器仍可能只收缩/平移可见视口。
 *
 * 这里把 VisualViewport 的真实高度和顶部偏移同步成整个应用根节点的 CSS 变量，作为
 * 标准声明之外的兼容兜底。所有一级页面因此始终贴合老板当前真正看得到的区域，而
 * 不是软键盘弹起前的布局高度；页面自身继续滚动，不再留下重复扣减后的底部白带。
 */

export interface VisualViewportSource {
  readonly height: number;
  readonly offsetTop: number;
  addEventListener(type: "resize" | "scroll", listener: EventListener): void;
  removeEventListener(type: "resize" | "scroll", listener: EventListener): void;
}

export interface ViewportWindowSource {
  readonly innerHeight?: number;
  addEventListener(type: "orientationchange", listener: EventListener): void;
  removeEventListener(type: "orientationchange", listener: EventListener): void;
}

const HEIGHT_VARIABLE = "--herskin-visual-viewport-height";
const TOP_VARIABLE = "--herskin-visual-viewport-top";
const KEYBOARD_INSET_VARIABLE = "--herskin-keyboard-inset";
const KEYBOARD_OPEN_THRESHOLD = 96;

function isTextEntryElement(element: Element | null): boolean {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  if (tagName === "textarea") return true;
  if (tagName === "input") {
    const type = (element.getAttribute("type") ?? "text").toLowerCase();
    return ![
      "button",
      "checkbox",
      "color",
      "file",
      "hidden",
      "image",
      "radio",
      "range",
      "reset",
      "submit",
    ].includes(type);
  }
  return (
    element.getAttribute("contenteditable") === "" ||
    element.getAttribute("contenteditable") === "true"
  );
}

export function bindVisualViewportLayout(
  element: HTMLElement,
  viewport: VisualViewportSource | null = window.visualViewport,
  hostWindow: ViewportWindowSource = window,
): () => void {
  if (!viewport) return () => undefined;

  /*
   * 有些 Chromium 外壳会让 innerHeight 和 VisualViewport 一起缩小。若每帧直接拿
   * innerHeight 相减，键盘高度会错误地变成 0。绑定发生在登录后、输入框聚焦前，
   * 因此保存当前方向里见过的最大布局高度，能兼容 resizes-visual 与
   * resizes-content 两种实现；方向变化时再用新方向的尺寸重新建基线。
   */
  const currentLayoutHeight = () => {
    const viewportBottom = Math.max(0, viewport.height) + Math.max(0, viewport.offsetTop);
    return typeof hostWindow.innerHeight === "number" && Number.isFinite(hostWindow.innerHeight)
      ? Math.max(0, hostWindow.innerHeight)
      : viewportBottom;
  };
  let layoutHeightBaseline = Math.max(
    currentLayoutHeight(),
    Math.max(0, viewport.height) + Math.max(0, viewport.offsetTop),
  );
  let keyboardOpen = false;

  const sync = () => {
    // 部分浏览器在键盘动画的中间帧会短暂报告 0；保留上一帧可避免界面闪成一条线。
    if (Number.isFinite(viewport.height) && viewport.height > 0) {
      element.style.setProperty(HEIGHT_VARIABLE, `${viewport.height}px`);
    }
    const offsetTop = Number.isFinite(viewport.offsetTop) ? Math.max(0, viewport.offsetTop) : 0;
    element.style.setProperty(TOP_VARIABLE, `${offsetTop}px`);
    const layoutHeight = currentLayoutHeight();
    const viewportBottom = Math.max(0, viewport.height) + offsetTop;
    const keyboardInset = Math.max(
      0,
      Math.max(layoutHeightBaseline, layoutHeight) - viewportBottom,
    );
    const activeElement = element.ownerDocument?.activeElement ?? null;
    const hasTextEntryFocus = element.contains(activeElement) && isTextEntryElement(activeElement);

    // focusout 可能早于键盘收起动画；已打开状态要等视口恢复后再解除，避免底部跳动。
    keyboardOpen = keyboardInset >= KEYBOARD_OPEN_THRESHOLD && (hasTextEntryFocus || keyboardOpen);
    element.style.setProperty(KEYBOARD_INSET_VARIABLE, `${keyboardInset}px`);
    element.dataset.keyboardOpen = keyboardOpen ? "true" : "false";

    // 地址栏展开/收起也会改变可见高度，但没有文本输入焦点时不能误判成软键盘。
    if (!keyboardOpen && !hasTextEntryFocus) {
      layoutHeightBaseline = Math.max(layoutHeightBaseline, layoutHeight, viewportBottom);
    }
  };

  const resetOrientationBaseline = () => {
    layoutHeightBaseline = Math.max(
      currentLayoutHeight(),
      Math.max(0, viewport.height) + Math.max(0, viewport.offsetTop),
    );
    keyboardOpen = false;
    sync();
  };

  sync();
  viewport.addEventListener("resize", sync);
  viewport.addEventListener("scroll", sync);
  element.addEventListener("focusin", sync);
  element.addEventListener("focusout", sync);
  hostWindow.addEventListener("orientationchange", resetOrientationBaseline);

  return () => {
    viewport.removeEventListener("resize", sync);
    viewport.removeEventListener("scroll", sync);
    element.removeEventListener("focusin", sync);
    element.removeEventListener("focusout", sync);
    hostWindow.removeEventListener("orientationchange", resetOrientationBaseline);
    element.style.removeProperty(HEIGHT_VARIABLE);
    element.style.removeProperty(TOP_VARIABLE);
    element.style.removeProperty(KEYBOARD_INSET_VARIABLE);
    delete element.dataset.keyboardOpen;
  };
}
